import { fetchLandingNotification, saveLandingNotification, testLandingNotification } from '@/api/landings';
import LoadingWrapper from '@/components/loading-wrapper';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { InputGroup, InputGroupInput } from '@/components/ui/input-group';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { PageScroll } from '@/layouts/page-layout';
import LandingsGate from '@/pages/landings/landings-gate';
import { cn } from '@/lib/utils';
import { BellIcon } from 'lucide-react';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { toast } from 'sonner';

export default function LandingsNotificationPage() {
	return (
		<PageScroll contentClassName='p-2 flex flex-col gap-3'>
			<Card className='gap-0 py-0 shadow-sm overflow-hidden'>
				<CardHeader className='border-b px-4 py-4 flex'>
					<div className='flex items-center gap-3'>
						<div className='rounded-lg bg-chart-2/15 p-2'>
							<BellIcon className='text-chart-2' size={20} />
						</div>
						<div>
							<CardTitle className='text-chart-2'>Уведомления Telegram</CardTitle>
							<CardDescription>Бот и чат для событий лендингов</CardDescription>
						</div>
					</div>
				</CardHeader>
				<CardContent className='px-4 py-4'>
					<LandingsGate>
						<NotificationForm />
					</LandingsGate>
				</CardContent>
			</Card>
		</PageScroll>
	);
}

function NotificationForm() {
	const [configured, setConfigured] = useState(false);
	const [botToken, setBotToken] = useState('');
	const [chatId, setChatId] = useState('');
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [testing, setTesting] = useState(false);

	const loadStatus = useCallback(async () => {
		setLoading(true);
		try {
			const data = await fetchLandingNotification();
			setConfigured(data.configured);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		void loadStatus();
	}, [loadStatus]);

	async function handleSubmit(e: FormEvent) {
		e.preventDefault();
		const trimmedToken = botToken.trim();
		const trimmedChatId = chatId.trim();
		if (!trimmedToken || !trimmedChatId) {
			toast.error('Укажите bot token и chat id');
			return;
		}

		setSaving(true);
		try {
			const result = await saveLandingNotification({ botToken: trimmedToken, chatId: trimmedChatId });
			setConfigured(true);
			setBotToken('');
			if (result.testSent) {
				toast.success('Настройки сохранены');
			} else {
				toast.warning(
					`Настройки сохранены, но тест не прошёл: ${result.testError ?? 'неизвестная ошибка'}`,
				);
			}
		} finally {
			setSaving(false);
		}
	}

	async function handleTest() {
		const trimmedToken = botToken.trim();
		const trimmedChatId = chatId.trim();
		if (!configured && (!trimmedToken || !trimmedChatId)) {
			toast.error('Укажите bot token и chat id для теста');
			return;
		}

		setTesting(true);
		try {
			if (trimmedToken && trimmedChatId) {
				await testLandingNotification({ botToken: trimmedToken, chatId: trimmedChatId });
			} else {
				await testLandingNotification();
			}
			toast.success('Тестовое сообщение отправлено');
		} finally {
			setTesting(false);
		}
	}

	if (loading) {
		return (
			<div className='flex justify-center py-8'>
				<Spinner />
			</div>
		);
	}

	return (
		<form className='flex flex-col gap-4' onSubmit={handleSubmit}>
			<div className='flex items-center gap-2'>
				<span
					className={cn(
						'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
						configured
							? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
							: 'bg-muted text-muted-foreground',
					)}
				>
					{configured ? 'Настроено' : 'Не настроено'}
				</span>
			</div>

			<p className='text-sm text-muted-foreground'>
				{configured
					? 'Уведомления работают. Токен не отображается — введите новый, чтобы заменить.'
					: 'Укажите бота и чат для получения событий connect, download и form.'}
			</p>

			<div className='flex flex-col gap-1.5'>
				<Label htmlFor='landing-bot-token'>Bot Token</Label>
				<InputGroup>
					<InputGroupInput
						id='landing-bot-token'
						type='password'
						autoComplete='off'
						placeholder={configured ? '••••••••' : '123456:ABC...'}
						value={botToken}
						onChange={e => setBotToken(e.target.value)}
						disabled={saving || testing}
					/>
				</InputGroup>
			</div>

			<div className='flex flex-col gap-1.5'>
				<Label htmlFor='landing-chat-id'>Chat ID</Label>
				<InputGroup>
					<InputGroupInput
						id='landing-chat-id'
						placeholder='-1001234567890'
						value={chatId}
						onChange={e => setChatId(e.target.value)}
						disabled={saving || testing}
					/>
				</InputGroup>
				<p className='text-xs text-muted-foreground'>ID группы или личного чата, например -100...</p>
			</div>

			<div className='flex flex-wrap gap-2'>
				<LoadingWrapper show={saving}>
					<Button type='submit' disabled={saving || testing}>
						Сохранить
					</Button>
				</LoadingWrapper>
				<LoadingWrapper show={testing}>
					<Button type='button' variant='outline' disabled={saving || testing} onClick={() => void handleTest()}>
						Тест
					</Button>
				</LoadingWrapper>
			</div>
		</form>
	);
}
